/**
 * Auth context for SolidJS.
 *
 * Hydrates from sessionStorage on mount. Components consuming
 * `useAuth()` get reactive access to the current token + a small
 * action surface (login/logout). Token refresh is best-effort: the
 * provider runs a 60s timer that refreshes ~30s before expiry.
 */

import {
  type ParentComponent,
  createContext,
  createSignal,
  onCleanup,
  onMount,
  useContext,
} from "solid-js";

import {
  authConfigFromEnv,
  logout as oidcLogout,
  refreshTokens,
  startLogin,
} from "./oidc";
import { type StoredTokens, tokenStorage } from "./storage";

interface AuthContextValue {
  tokens: () => StoredTokens | null;
  isAuthenticated: () => boolean;
  login: () => Promise<void>;
  logout: () => void;
}

const AuthContext = createContext<AuthContextValue>();

export const AuthProvider: ParentComponent = (props) => {
  const cfg = authConfigFromEnv();
  const [tokens, setTokens] = createSignal<StoredTokens | null>(null);

  const sync = () => {
    setTokens(tokenStorage.load());
  };

  onMount(() => {
    sync();

    // sessionStorage doesn't fire 'storage' events for same-tab writes,
    // so OIDC code paths call sync() explicitly via window event.
    const handler = () => sync();
    window.addEventListener("z01:auth-changed", handler);
    onCleanup(() => window.removeEventListener("z01:auth-changed", handler));

    const interval = window.setInterval(async () => {
      const t = tokens();
      if (!t) return;
      if (tokenStorage.isExpired(t, 30_000)) {
        const ok = await refreshTokens(cfg);
        if (!ok) {
          tokenStorage.clear();
        }
        sync();
      }
    }, 60_000);
    onCleanup(() => window.clearInterval(interval));
  });

  const value: AuthContextValue = {
    tokens,
    isAuthenticated: () => {
      const t = tokens();
      return !!t && !tokenStorage.isExpired(t);
    },
    login: () => startLogin(cfg),
    logout: () => oidcLogout(cfg),
  };

  return (
    <AuthContext.Provider value={value}>{props.children}</AuthContext.Provider>
  );
};

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be inside <AuthProvider>");
  return ctx;
}

/** Notify the provider after token write/clear from outside React lifecycle. */
export function notifyAuthChanged(): void {
  window.dispatchEvent(new CustomEvent("z01:auth-changed"));
}
